import styled from 'styled-components';
import { useState } from 'react';
import { useErrorHandlerContext, useOpeningElementsControllerContext, useUserLanguage, useListNavigationStateContext } from '@/customHooks';
import { capitalizeFirstLetter } from '@/helpers';
import { GetSuppliersQuery, SupplierFilterInput, useDeleteSupplierMutation, useGetSuppliersQuery } from '@/graphql/generated';
import { ButtonWithDropDownMenu } from '../molecules';
import { FlattenedSuppliersData } from '@/custom-types';
import { CenterScreenModal, LoadingSpinner, StatusPill } from '../atoms';
import { useNavigate } from 'react-router';
import { pathURL } from '@/components/constants/urls';
import { Col, Row } from 'react-grid-system';
import { ConfirmationDialog } from '../organism';
import textsData from '@/textData/SupplierListTexts.json'
//import { useGetPhoneCodeFromCountryCode } from '@/customHooks';

type Suplier = NonNullable<GetSuppliersQuery['suppliers']>['items'][number]

type SupplierListTableProps = {
  filters: SupplierFilterInput;
};
const StyledGridTable = styled.div`
  padding-top: ${({ theme }) => theme.spacing.lg} ;
  padding-bottom: ${({ theme }) => theme.spacing.lg} ;
  border-radius: 12px;
  display: grid;
  grid-template-columns: 1fr 1fr 2fr 1fr 64px;
`;

const StyledGridHeader = styled.div`
  display: contents;
  & > div {
    font-weight: bold;
    text-align: left;
    padding: 15px;
    border-top: 1px solid grey;
    border-bottom: 1px solid gray;
  }
`;

const StyledGridRow = styled.div`
  display: contents;

  & > div {
    padding: 15px;
    background-color: ${({ theme }) => theme.colors.light};
    font-size: 14px;
    padding-top: 1rem;
    padding-bottom: 1rem;
    align-self: stretch;
    place-content: center;
    border-bottom: 1px solid lightgray;
  }

  &:nth-of-type(odd) > div {
    background-color: ${({ theme }) => theme.colors.background2};
  }
`;

const dataFlatter = (suppliers: Suplier[] | undefined): FlattenedSuppliersData[]  => {
  if (!suppliers || suppliers.length === 0) return []

  return suppliers.map((supplier) => ({
    firstName: supplier.firstName,
    lastName: supplier.lastName,
    phoneNumber: supplier.phoneNumber,
    isActive: supplier.isActive,
    id: supplier.id,
    email: '',
    street: supplier.address.street,
    city: supplier.address.city?.name ?? '',
    state: supplier.address.state?.name ?? '',
  }))
}

export const SupplierListTable = ({ filters }: SupplierListTableProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()

  const { open: errorHandle } = useErrorHandlerContext()
  const { setListState } = useListNavigationStateContext();

  const navigate = useNavigate()
  
  const [deleteSupplier] = useDeleteSupplierMutation()
  const [supplierIdToDelete, setSupplierIdToDelete] = useState<string | null>(null)
  
  const {data, error, refetch, loading} = useGetSuppliersQuery({
    variables: {
      filterInput: filters,
    },
    fetchPolicy: 'network-only',
  })
  
  const tableData = dataFlatter(data?.suppliers?.items)

  const confirmDeleteHandle = () => {
    if (!supplierIdToDelete) return

    deleteSupplier({ variables: { id: supplierIdToDelete } })
    .then((response) => {
      confirmDialogModalOpen({
        title: texts.deleteConfirmation.title,
        description: `${response.data?.deleteSupplier.firstName} ${texts.deleteConfirmation.description}`,
      })
      refetch()
    })
    .catch(errorHandle)
    .finally(() => setSupplierIdToDelete(null))
  }

  const onDeleteHandle = (id: string) => {
    setSupplierIdToDelete(id)
  }

  const onEditHandle = (id: string) => {
    navigate(`/${pathURL.USER}/${pathURL.SUPPLIER}/${pathURL.CREATE}`, {
      state: {
        supplierId: id,
        readOnly: false,
      }
    })
  }

  const onDetailConsultingHandle = (id: string) => {
    setListState('suppliers', {
      currentPage: parseInt(filters.page || '1'),
      filters,
      selectedItemId: id,
    });
    navigate(`/${pathURL.USER}/${pathURL.SUPPLIER}/${pathURL.CREATE}`, {
      state: {
        supplierId: id,
        readOnly: true,
      }
    })
  }

  if (loading) {
    return (
      <Row justify='center'>
        <Col xs='content' >
          <LoadingSpinner text/>
        </Col>
      </Row>
    )
  }

  if (tableData.length === 0) {
    return (
      <Row>
        <Col>
          <span><b>{texts.noData}</b></span>
        </Col>
      </Row>
    )
  }

  if (error) errorHandle(error)

  return (
    <>
      <StyledGridTable>
        {/* Encabezado */}
        <StyledGridHeader>
          <div>{texts.name}</div>
          <div>{texts.phone}</div>
          <div>{texts.location}</div>
          <div>{texts.status}</div>
          <div></div>
        </StyledGridHeader>

        {/* Filas dinámicas */}
        {tableData.map((data) => (
          <StyledGridRow key={data.id}>
            <div>{`${capitalizeFirstLetter(data.firstName)} ${capitalizeFirstLetter(data.lastName)}`}</div>
            <div>{data.phoneNumber}</div>
            <div>{`${data.street} ${data.city ? `, ${data.city}` : ''} ${data.state ? `, ${data.state}` : ''}`}</div>
            <div><StatusPill isActive={data.isActive} /></div>
            <div>
              <ButtonWithDropDownMenu
                options={[
                  {item: texts.dropdownMenu.deatils, action: () => onDetailConsultingHandle(data.id)},
                  {item: texts.dropdownMenu.edit, action: () => onEditHandle(data.id)},
                  {item: texts.dropdownMenu.delete, action: () => onDeleteHandle(data.id)},
                ]}
                position='left'
              />
            </div>
          </StyledGridRow>
        ))}
      </StyledGridTable>

      <CenterScreenModal
        isOpen={supplierIdToDelete !== null}
        onClose={() => setSupplierIdToDelete(null)}
      >
        <ConfirmationDialog
          title={texts.deletePrompt.title}
          description={texts.deletePrompt.description}
          confirmButton={{
            action: confirmDeleteHandle,
            text: texts.deletePrompt.confirm,
          }}
          cancelButton={{
            action: () => setSupplierIdToDelete(null),
            text: texts.deletePrompt.cancel,
          }}
        />
      </CenterScreenModal>
    </>
  );
};